import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { scansApi, patientsApi, generationApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Baby,
  Save,
  RotateCcw,
  Ruler,
  Calendar,
  Download,
  Circle,
  CheckCircle2
} from 'lucide-react';
import { ShareActions } from '../../components/shared/ShareActions';

// Haftalık referans verileri
const FETAL_DATA = {
  18: { bpd: { p5: 34.6, p50: 39.5, p95: 44.4 }, hc: { p5: 134.7, p50: 151.1, p95: 167.5 } },
  19: { bpd: { p5: 37.8, p50: 43.0, p95: 48.2 }, hc: { p5: 147.1, p50: 164.1, p95: 181.1 } },
  20: { bpd: { p5: 41.0, p50: 46.4, p95: 51.8 }, hc: { p5: 159.2, p50: 176.8, p95: 194.4 } },
  21: { bpd: { p5: 44.1, p50: 49.7, p95: 55.3 }, hc: { p5: 171.0, p50: 189.2, p95: 207.4 } },
  22: { bpd: { p5: 47.2, p50: 53.0, p95: 58.8 }, hc: { p5: 182.5, p50: 201.3, p95: 220.1 } },
  23: { bpd: { p5: 50.1, p50: 56.2, p95: 62.3 }, hc: { p5: 193.6, p50: 213.0, p95: 232.4 } },
  24: { bpd: { p5: 53.0, p50: 59.3, p95: 65.6 }, hc: { p5: 204.4, p50: 224.4, p95: 244.4 } },
  25: { bpd: { p5: 55.8, p50: 62.3, p95: 68.8 }, hc: { p5: 214.8, p50: 235.4, p95: 256.0 } },
  26: { bpd: { p5: 58.6, p50: 65.3, p95: 72.0 }, hc: { p5: 224.8, p50: 246.0, p95: 267.2 } },
  27: { bpd: { p5: 61.1, p50: 68.1, p95: 75.1 }, hc: { p5: 234.4, p50: 256.2, p95: 278.0 } },
  28: { bpd: { p5: 63.6, p50: 70.8, p95: 78.0 }, hc: { p5: 243.7, p50: 266.1, p95: 288.5 } },
  29: { bpd: { p5: 66.1, p50: 73.5, p95: 80.9 }, hc: { p5: 252.5, p50: 275.5, p95: 298.5 } },
  30: { bpd: { p5: 68.4, p50: 76.0, p95: 83.6 }, hc: { p5: 260.8, p50: 284.4, p95: 308.0 } },
  31: { bpd: { p5: 70.5, p50: 78.4, p95: 86.3 }, hc: { p5: 268.7, p50: 292.9, p95: 317.1 } },
  32: { bpd: { p5: 72.6, p50: 80.7, p95: 88.8 }, hc: { p5: 276.1, p50: 300.9, p95: 325.7 } },
  33: { bpd: { p5: 74.6, p50: 82.9, p95: 91.2 }, hc: { p5: 283.0, p50: 308.4, p95: 333.8 } },
  34: { bpd: { p5: 76.5, p50: 85.0, p95: 93.5 }, hc: { p5: 289.5, p50: 315.5, p95: 341.5 } },
  35: { bpd: { p5: 78.3, p50: 87.0, p95: 95.7 }, hc: { p5: 295.4, p50: 322.0, p95: 348.6 } },
  36: { bpd: { p5: 79.8, p50: 88.8, p95: 97.8 }, hc: { p5: 300.7, p50: 327.9, p95: 355.1 } },
  37: { bpd: { p5: 81.3, p50: 90.5, p95: 99.7 }, hc: { p5: 305.5, p50: 333.3, p95: 361.1 } },
  38: { bpd: { p5: 82.7, p50: 92.1, p95: 101.5 }, hc: { p5: 309.8, p50: 338.2, p95: 366.6 } },
  39: { bpd: { p5: 83.9, p50: 93.5, p95: 103.1 }, hc: { p5: 313.5, p50: 342.5, p95: 371.5 } },
  40: { bpd: { p5: 84.9, p50: 94.8, p95: 104.7 }, hc: { p5: 316.5, p50: 346.1, p95: 375.7 } }
};

// Parça grupları - sıralı çizim için
const PART_GROUPS = [
  {
    id: 'head',
    title: '1. Kafa',
    icon: '🟡',
    keys: ['bpd', 'hc'],
    description: 'Kafa şekli oluşur'
  },
  {
    id: 'forehead',
    title: '2. Alın',
    icon: '🟠',
    keys: ['fromen'],
    description: 'Alın detayı eklenir'
  },
  {
    id: 'eyes',
    title: '3. Gözler',
    icon: '🟢',
    keys: ['goztepe', 'goz'],
    description: 'Gözler çizilir'
  },
  {
    id: 'nose',
    title: '4. Burun',
    icon: '🔵',
    keys: ['burun'],
    description: 'Burun eklenir'
  },
  {
    id: 'mouth',
    title: '5. Ağız',
    icon: '🟣',
    keys: ['agiz_capi', 'ust_dudak'],
    description: 'Ağız ve dudaklar'
  },
  {
    id: 'chin',
    title: '6. Çene',
    icon: '🔴',
    keys: ['cene'],
    description: 'Çene tamamlanır'
  },
  {
    id: 'cheeks',
    title: '7. Yanaklar',
    icon: '🩷',
    keys: ['bioc_cap'],
    description: 'Yanak kızarıklığı'
  }
];

// Ölçü ayarları
const MEASUREMENT_CONFIG = {
  bpd: { label: 'BPD (Kafa Genişliği)', unit: 'mm', step: 0.5, baseMin: 30, baseMax: 110 },
  hc: { label: 'HC (Kafa Çevresi)', unit: 'mm', step: 1, baseMin: 120, baseMax: 400 },
  fromen: { label: 'Alın Yüksekliği', unit: 'mm', step: 0.5, baseMin: 15, baseMax: 50 },
  goztepe: { label: 'Göz Arası Mesafe', unit: 'mm', step: 0.5, baseMin: 10, baseMax: 40 },
  goz: { label: 'Göz Boyutu', unit: 'mm', step: 0.5, baseMin: 5, baseMax: 20 },
  burun: { label: 'Burun Uzunluğu', unit: 'mm', step: 0.5, baseMin: 4, baseMax: 20 },
  bioc_cap: { label: 'Yüz Genişliği', unit: 'mm', step: 0.5, baseMin: 25, baseMax: 70 },
  agiz_capi: { label: 'Ağız Genişliği', unit: 'mm', step: 0.5, baseMin: 6, baseMax: 25 },
  ust_dudak: { label: 'Üst Dudak', unit: 'mm', step: 0.5, baseMin: 3, baseMax: 15 },
  cene: { label: 'Çene Uzunluğu', unit: 'mm', step: 0.5, baseMin: 10, baseMax: 40 }
};

// Boş başlangıç değerleri
const EMPTY_MEASUREMENTS = {
  bpd: null,
  hc: null,
  fromen: null,
  goztepe: null,
  goz: null,
  burun: null,
  bioc_cap: null,
  agiz_capi: null,
  ust_dudak: null,
  cene: null
};

export const ParametricEditorPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');
  const debounceRef = useRef(null);

  // States
  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  const [selectedWeek, setSelectedWeek] = useState(23);
  const [measurements, setMeasurements] = useState(EMPTY_MEASUREMENTS);
  const [gender, setGender] = useState('female');

  // Hangi parçalar tamamlandı?
  const getCompletedParts = useCallback(() => {
    return PART_GROUPS.filter(group => 
      group.keys.some(key => measurements[key] !== null && measurements[key] !== '' && measurements[key] !== 0)
    ).map(g => g.id);
  }, [measurements]);

  // Fetch scan data
  useEffect(() => {
    const fetchData = async () => {
      if (!scanId) {
        toast.error('Tarama ID bulunamadı');
        navigate('/doctor/patients');
        return;
      }

      try {
        setLoading(true);
        const scanData = await scansApi.get(scanId);
        setScan(scanData);

        const patientData = await patientsApi.get(scanData.patient_id);
        setPatient(patientData);

        setSelectedWeek(patientData.pregnancy_week || 23);

        // Kayıtlı ölçüler varsa yükle
        if (scanData.metadata?.measurements) {
          setMeasurements(prev => ({ ...prev, ...scanData.metadata.measurements }));
        }
        if (scanData.metadata?.gender) {
          setGender(scanData.metadata.gender);
        }
      } catch (error) {
        toast.error('Veri yüklenirken hata oluştu');
        navigate('/doctor/patients');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [scanId, navigate]);

  // Auto-generate when measurements change (debounced)
  useEffect(() => {
    // Hiç değer yoksa generate etme
    const hasAnyValue = Object.values(measurements).some(v => v !== null && v !== '' && v !== 0);
    
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      if (!hasAnyValue) {
        setGeneratedImage(null);
        return;
      }

      setGenerating(true);
      try {
        const result = await generationApi.parametricFace(measurements, gender, 'png');
        setGeneratedImage(result.image_data);
      } catch (error) {
        console.error('Generation error:', error);
      } finally {
        setGenerating(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [measurements, gender]);

  // Measurement change handler
  const handleMeasurementChange = useCallback((key, value) => {
    if (value === '' || value === null) {
      setMeasurements(prev => ({ ...prev, [key]: null }));
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      const config = MEASUREMENT_CONFIG[key];
      const clampedValue = Math.max(config.baseMin, Math.min(config.baseMax, numValue));
      setMeasurements(prev => ({ ...prev, [key]: clampedValue }));
    }
  }, []);

  // Slider change - değer varsa güncelle, yoksa başlat
  const handleSliderChange = useCallback((key, value) => {
    const config = MEASUREMENT_CONFIG[key];
    // Slider kaydırıldığında değer ata
    const newValue = Math.max(config.baseMin, Math.min(config.baseMax, value));
    setMeasurements(prev => ({ ...prev, [key]: newValue }));
  }, []);

  // Reset
  const handleReset = useCallback(() => {
    setMeasurements(EMPTY_MEASUREMENTS);
    setGeneratedImage(null);
    toast.info('Tüm ölçüler sıfırlandı');
  }, []);

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      await scansApi.update(scanId, {
        metadata: { measurements, gender, week: selectedWeek }
      });
      toast.success('Kaydedildi!');
    } catch (error) {
      toast.error('Kaydetme hatası');
    } finally {
      setSaving(false);
    }
  };

  // Download
  const handleDownload = useCallback(async () => {
    if (!generatedImage) return;
    
    try {
      // PNG formatında indir
      const result = await generationApi.parametricFace(measurements, gender, 'png');
      const link = document.createElement('a');
      link.href = result.image_data;
      link.download = `bebek_${patient?.first_name || 'bebek'}_${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Görsel indirildi');
    } catch (error) {
      toast.error('İndirme hatası');
    }
  }, [generatedImage, measurements, gender, patient]);

  // Get slider value (null ise ortayı göster ama aktif değil)
  const getSliderValue = (key) => {
    const val = measurements[key];
    if (val === null || val === '' || val === 0) {
      const config = MEASUREMENT_CONFIG[key];
      return (config.baseMin + config.baseMax) / 2;
    }
    return val;
  };

  // Input display value
  const getInputValue = (key) => {
    const val = measurements[key];
    if (val === null || val === '' || val === 0) return '';
    return val.toFixed(1);
  };

  const completedParts = getCompletedParts();

  if (loading) {
    return (
      <DashboardLayout title="Parametrik Çizim">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Parametrik Bebek Yüzü" 
      subtitle={patient ? `${patient.first_name} ${patient.last_name}` : ''}
    >
      <div className="h-[calc(100vh-180px)] flex flex-col" data-testid="parametric-editor-page">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <Button 
            variant="ghost" 
            onClick={() => navigate(`/doctor/patients/${patient?.id}`)}
            className="text-slate-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Geri
          </Button>
          
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleReset} size="sm">
              <RotateCcw className="w-4 h-4 mr-2" />
              Sıfırla
            </Button>
            <Button onClick={handleSave} disabled={saving} variant="outline" size="sm">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Kaydet
            </Button>
            {generatedImage && (
              <Button onClick={handleDownload} size="sm" className="bg-[#5A67D8] hover:bg-[#4F46E5]">
                <Download className="w-4 h-4 mr-2" />
                PNG İndir
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
          {/* Left Panel - Controls */}
          <div className="col-span-5 flex flex-col min-h-0">
            <Card className="flex-1 border-0 shadow-lg overflow-hidden flex flex-col">
              <CardHeader className="py-3 px-4 border-b bg-slate-50 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Ruler className="w-4 h-4 text-[#5A67D8]" />
                    Biyometrik Ölçüler
                  </CardTitle>
                  <span className="text-xs text-slate-500">
                    {completedParts.length}/7 parça
                  </span>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 overflow-y-auto p-4 space-y-5">
                {/* Week & Gender */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      Gebelik Haftası
                    </Label>
                    <Select value={selectedWeek.toString()} onValueChange={(v) => setSelectedWeek(parseInt(v))}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 23 }, (_, i) => i + 18).map((week) => (
                          <SelectItem key={week} value={week.toString()}>{week}. Hafta</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500 mb-1.5 block">Cinsiyet</Label>
                    <div className="flex gap-1">
                      <button
                        onClick={() => setGender('female')}
                        className={`flex-1 py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                          gender === 'female'
                            ? 'border-pink-400 bg-pink-50 text-pink-600'
                            : 'border-slate-200 hover:border-pink-200'
                        }`}
                      >
                        ♀ Kız
                      </button>
                      <button
                        onClick={() => setGender('male')}
                        className={`flex-1 py-2 px-2 rounded-lg border text-xs font-medium transition-all ${
                          gender === 'male'
                            ? 'border-blue-400 bg-blue-50 text-blue-600'
                            : 'border-slate-200 hover:border-blue-200'
                        }`}
                      >
                        ♂ Erkek
                      </button>
                    </div>
                  </div>
                </div>

                {/* Part Groups */}
                {PART_GROUPS.map((group) => {
                  const isComplete = completedParts.includes(group.id);
                  
                  return (
                    <div key={group.id} className={`p-3 rounded-lg border transition-all ${
                      isComplete 
                        ? 'border-green-200 bg-green-50/50' 
                        : 'border-slate-200 bg-white'
                    }`}>
                      {/* Group Header */}
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm">{group.icon}</span>
                          <span className="font-medium text-sm text-slate-700">{group.title}</span>
                        </div>
                        {isComplete ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <Circle className="w-4 h-4 text-slate-300" />
                        )}
                      </div>
                      
                      {/* Inputs */}
                      <div className="space-y-3">
                        {group.keys.map((key) => {
                          const config = MEASUREMENT_CONFIG[key];
                          const hasValue = measurements[key] !== null && measurements[key] !== '' && measurements[key] !== 0;
                          
                          return (
                            <div key={key} className="space-y-1">
                              <div className="flex items-center justify-between">
                                <Label className="text-xs text-slate-600">{config.label}</Label>
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="number"
                                    value={getInputValue(key)}
                                    onChange={(e) => handleMeasurementChange(key, e.target.value)}
                                    placeholder="-"
                                    className={`w-16 h-6 text-xs text-right px-1 ${
                                      hasValue ? 'border-green-300' : 'border-slate-200'
                                    }`}
                                    step={config.step}
                                    min={config.baseMin}
                                    max={config.baseMax}
                                  />
                                  <span className="text-xs text-slate-400 w-6">{config.unit}</span>
                                </div>
                              </div>
                              <Slider
                                value={[getSliderValue(key)]}
                                min={config.baseMin}
                                max={config.baseMax}
                                step={config.step}
                                onValueChange={([val]) => handleSliderChange(key, val)}
                                className={`cursor-pointer ${hasValue ? 'opacity-100' : 'opacity-40'}`}
                              />
                              <div className="flex justify-between text-[10px] text-slate-400">
                                <span>{config.baseMin}</span>
                                <span>{config.baseMax}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Right Panel - Preview */}
          <div className="col-span-7 flex flex-col min-h-0">
            <Card className="flex-1 border-0 shadow-lg overflow-hidden flex flex-col bg-gradient-to-b from-[#FFF8F5] to-[#FFF0EB]">
              <CardHeader className="py-3 px-4 border-b border-slate-200/50 flex-shrink-0 bg-white/50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2 text-[#5A67D8]">
                    <Baby className="w-4 h-4" />
                    Canlı Önizleme
                    {generating && <Loader2 className="w-3 h-3 animate-spin ml-2" />}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-xs text-slate-500">
                    {completedParts.length > 0 && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded">
                        {completedParts.length} parça çizildi
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 p-0 overflow-hidden flex items-center justify-center">
                {generatedImage ? (
                  <img
                    src={generatedImage}
                    alt="Parametrik Bebek Yüzü"
                    className="max-w-full max-h-full object-contain"
                    data-testid="parametric-face-image"
                  />
                ) : (
                  <div className="text-center p-8">
                    <div className="w-32 h-32 mx-auto mb-4 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center">
                      <Baby className="w-12 h-12 text-slate-300" />
                    </div>
                    <p className="text-slate-500 mb-1">Boş Sahne</p>
                    <p className="text-sm text-slate-400">
                      Soldaki ölçüleri girerek yüzü oluşturmaya başlayın
                    </p>
                    <div className="mt-4 text-xs text-slate-400">
                      Kafa → Alın → Gözler → Burun → Ağız → Çene → Yanaklar
                    </div>
                  </div>
                )}
              </CardContent>
              
              {/* Bottom status */}
              <div className="px-4 py-2 border-t border-slate-200/50 flex-shrink-0 bg-white/30">
                {generatedImage && (
                  <div className="mb-2">
                    <ShareActions
                      imageBase64={generatedImage}
                      patientPhone={patient?.phone}
                      patientName={`${patient?.first_name || ''} ${patient?.last_name || ''}`}
                      generationType="parametrik"
                    />
                  </div>
                )}
                <div className="flex items-center justify-between text-xs">
                  <span className={`font-medium ${gender === 'female' ? 'text-pink-500' : 'text-blue-500'}`}>
                    {gender === 'female' ? '♀ Kız' : '♂ Erkek'} • {selectedWeek}. hafta
                  </span>
                  <span className="text-slate-400">
                    Real-time SVG Çizim
                  </span>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ParametricEditorPage;
